/**
 * V109-6-5 — client for the program Assessment Results exports (ADR-0091). Every call is
 * workspace-scoped (`/api/w/:slug/programs/:program/assessment-results`): the server resolves the
 * workspace and the caller from the session; members list and download, the program lead or an
 * owner / admin generates. The workspace sweep is started at `/api/w/:slug/scans` (owner / admin).
 */
import { ApiError, req } from './api';

const base = (slug: string, program: string) => `/api/w/${encodeURIComponent(slug)}/programs/${encodeURIComponent(program)}/assessment-results`;

export type AssessmentExportKind = 'oscal_ar_program' | 'oscal_ar_control' | 'oscal_poam';

export interface AssessmentExport {
  package_id: string;
  kind: AssessmentExportKind;
  schema_version: string;
  source_run_id: string;
  scope: { control_ids: string[]; system_id: string | null; period_id: string };
  valid: boolean;
  content_sha256: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  verify_path: string;
  download_path: string;
}

export interface AssessmentExportsView {
  latest_sweep: { run_id: string; state: 'complete' | 'partial'; started_at: string; finished_at: string } | null;
  can_generate: boolean;
  exports: AssessmentExport[];
}

export const listAssessmentExports = (slug: string, program: string) => req<AssessmentExportsView>(base(slug, program));

export const generateAssessmentExport = (slug: string, program: string, body: { kind: AssessmentExportKind; run_id?: string; requirement_id?: string; system_id?: string }) =>
  req<{ package: { package_id: string; kind: AssessmentExportKind }; download_path: string; verify_path: string }>(base(slug, program), { method: 'POST', body: JSON.stringify(body) });

/** Start an on-demand workspace sweep (owner / admin). */
export const startWorkspaceSweep = (slug: string) => req<unknown>(`/api/w/${encodeURIComponent(slug)}/scans`, { method: 'POST', body: '{}' });

/** Download the signed OSCAL JSON (the exact stored bytes) and hand it to the browser. */
export async function downloadAssessmentExport(path: string): Promise<void> {
  const res = await fetch(path);
  if (!res.ok) throw new ApiError(res.status, res.status === 404 ? 'The export could not be found.' : 'The export could not be downloaded.');
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'assessment-results.json';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
